import BaseActionAbstract from 'cio/app-models/action-abstracts/base';
import { computed } from '@ember/object';
import { isBlank } from '@ember/utils';
import { task } from 'ember-concurrency';
import { isAutoRetryWindowValueValid } from 'cio/utils/message-auto-retry';

const EDITABLE_PROPERTIES = [
  'name',
  'preconditions',
  'sendingState',
  'tracked',
  'sendToPlatform',
  'sendToUnsubscribed',
  'useMessageLimits',
  'overrideMessageLimitsRetry',
  'messageLimitsRetryWindowSecs',
  'canConvert',
  'holdout',
  'subscriptionTopicId',
];

const DIRTY_TRACKED_PROPERTIES = [
  'name',
  'preconditions',
  'sendingState',
  'tracked',
  'sendToPlatform',
  'sendToUnsubscribed',
  'useMessageLimits',
  'overrideMessageLimitsRetry',
  'messageLimitsRetryWindowSecs',
  'attributesMappings.[]',
  'canConvert',
  'holdout',
  'subscriptionTopicId',
];

const DIRTY_DEPENDENCIES = `action.{${DIRTY_TRACKED_PROPERTIES.join(',')}}`;

export default BaseActionAbstract.extend({
  type: 'message',

  action: null,
  actionType: null,

  _editableProperties: EDITABLE_PROPERTIES,
  _dirtyDependencies: DIRTY_DEPENDENCIES,

  name: null,
  preconditions: null,
  sendingState: null,
  tracked: null,
  sendToPlatform: null,
  sendToUnsubscribed: null,
  useMessageLimits: null,
  overrideMessageLimitsRetry: null,
  messageLimitsRetryWindowSecs: 0,
  attributesMappings: null,
  canConvert: null,
  holdout: null,
  subscriptionTopicId: null,

  _isDirty: computed(
    'attributesMappings.@each.{name,value}',
    'action.attributesNames.[]',
    'action.attributesValues.[]',
    function () {
      if (!this.attributesMappings || !this.action.attributesNames || !this.action.attributesValues) {
        return false;
      }

      return (
        this.attributesMappings.length !== this.action.attributesNames.length ||
        this.attributesMappings.some(({ name, value }, index) => {
          return name !== this.action.attributesNames[index] || value !== this.action.attributesValues[index];
        })
      );
    },
  ),

  prepareForEdit() {
    this._super(...arguments);

    if (this.action.type === 'webhook_action') {
      this.set(
        'attributesMappings',
        (this.action.attributesNames || []).map((name, index) => ({
          name,
          value: this.action.attributesValues[index],
        })),
      );
    }
  },

  prepareForSave() {
    let action = this._super(...arguments);

    if (this.action.type === 'webhook_action') {
      action.setProperties({
        attributesNames: this.attributesMappings.map((mapping) => mapping.name),
        attributesValues: this.attributesMappings.map((mapping) => mapping.value),
      });
    }

    return action;
  },

  rollback() {
    this.prepareForEdit();
  },

  errors: computed('name', 'action.type', 'messageLimitsRetryWindowSecs', function () {
    if (isBlank(this.name) && this.action.type !== 'collection_query_action') {
      return {
        name: ["Name can't be blank"],
      };
    }

    if (!isAutoRetryWindowValueValid(this.messageLimitsRetryWindowSecs)) {
      return {
        autoRetryWindowSecs: ['Incorrect auto-retry window timeframe.'],
      };
    }

    return;
  }),

  // eslint-disable-next-line require-yield
  saveTask: task(function* () {
    let action = this.prepareForSave();

    try {
      let response = yield action.save();
      this.prepareForEdit();
      return response;
    } catch (e) {
      action.rollbackAttributes();
      throw e;
    }
  }).drop(),

  // eslint-disable-next-line require-yield
  deleteTask: task(function* () {
    return this.action.campaign.removeActionFromWorkflow(this.action);
    // return yield this.action.deleteViaAPI();
  }).drop(),

  createSplitTask: task(function* () {
    return yield this.action.createSplit();
  }).drop(),
});
